import { Navigate, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { NocFnbView, nocFnbSearchShape } from "../../components/fnb/noc-view";
import { FnbOverview } from "../../components/fnb/overview";
import { ScanStation } from "../../components/fnb/scan-station";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { canScanMeals, fnbView } from "../../lib/fnb/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { isYmd, todayIn } from "../../lib/travel/view";

/**
 * `/logistics/food-beverages` — what it shows depends on who asks (`fnbView`):
 *   host    the Overview (her `fnbOverview`), `day` = the day shown
 *   noc     where the delegation eats + its packed meals (`NocFnbView`)
 *   scan    the scan station, the whole page (a door login)
 *   orders  sent to the Takeaway orders tab
 *
 * URL: `day` (games' zone, absent = today), plus the NOC's own keys
 * (`nocFnbSearchShape`). Lenient: a hand-edited value falls back to "not set".
 */
export const Route = createFileRoute("/_app/logistics/food-beverages/")({
	validateSearch: z.object({
		day: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}$/)
			.optional()
			.catch(undefined),
		...nocFnbSearchShape,
	}),
	component: FnbIndex,
});

function FnbIndex() {
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	const view = fnbView(session);
	if (view === "orders") return <Navigate to="/logistics/food-beverages/orders" search={(s: { games_id?: string }) => ({ games_id: s.games_id })} replace />;
	if (!gamesId || !timeZone) return <PageSkeleton />;
	if (view === "noc") return <NocFnbView session={session} gamesId={gamesId} timeZone={timeZone} search={search} navigate={navigate} />;
	if (view === "scan")
		return (
			<div className="mx-auto w-full max-w-[40rem] rounded-[0.625rem] border border-gms-line bg-base-100 p-[1rem]">
				<ScanStation gamesId={gamesId} page />
			</div>
		);
	const today = todayIn(timeZone);
	return (
		<FnbOverview
			gamesId={gamesId}
			gamesIdInUrl={search.games_id}
			today={today}
			date={isYmd(search.day) ? search.day : today}
			onDate={(d) => void navigate({ search: (s) => ({ ...s, day: d === today ? undefined : d }), replace: true })}
			canScan={canScanMeals(session)}
		/>
	);
}
